import { Link, useLocation, useNavigate, useFetcher } from '@remix-run/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faMagnifyingGlass, faHeart, faListCheck, faChartLine, faCalendar } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import LinkButton from "~/ui-apps/design-system/atoms/LinkButton";
import { useState, useEffect, useRef } from 'react';
import MainAuthModal from "~/ui-apps/design-system/organisms/auth-modals/App";

type NavLink = {
    to: string;
    icon: IconDefinition;
    label: string;
    isActive: (pathname: string, search?: string) => boolean;
    iconClassName?: string;
    disabled?: boolean;
    disabledLabel?: string;
    /** When true, signed-out users open sign-in modal instead of navigating */
    requireAuth?: boolean;
};

const NAV_LINKS: NavLink[] = [
    {
        to: '/screener',
        icon: faSliders,
        label: 'Screener',
        isActive: (p) => p === '/screener' || p.startsWith('/screener/'),
    },
    {
        to: '/evaluation-criteria',
        icon: faListCheck,
        label: 'Evaluation Criteria',
        isActive: (p) => p === '/evaluation-criteria',
    },
    {
        to: '/favorites',
        icon: faHeart,
        label: 'Favorites',
        isActive: (p) => p === '/favorites' || p.startsWith('/favorites/'),
        requireAuth: true,
    },
    {
        to: '/price-models',
        icon: faChartLine,
        label: 'Price Forecasts',
        isActive: (p) => p === '/price-models' || p.startsWith('/price-models/'),
        requireAuth: true,
    },
    {
        to: '/earnings-calendar',
        icon: faCalendar,
        label: 'Earnings calendar',
        isActive: (p) => p === '/earnings-calendar',
    },
];

const SignedOutSideNavbar = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const fetcher = useFetcher();
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

    const [searchSuggestions, setSearchSuggestions] = useState<Array<{
        ticker_symbol: string;
        name: string;
        market_cap: number;
        logo_url: string;
        ticker_id: string;
        exchange: string;
        company_id: string;
    }>>([]);
    const [searchValue, setSearchValue] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const debounce = (func: Function, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    const handleSearch = debounce((value: string) => {
        if (!value.trim()) {
            setSearchSuggestions([]);
            return;
        }
        fetcher.load(`/api/tickers?search_term=${encodeURIComponent(value)}`);
    }, 300);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        setActiveSearchIndex(-1);
        handleSearch(value);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (searchSuggestions.length === 0) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveSearchIndex(prev =>
                    prev < searchSuggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveSearchIndex(prev =>
                    prev > 0 ? prev - 1 : -1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (activeSearchIndex >= 0) {
                    handleSelectStock(searchSuggestions[activeSearchIndex]);
                }
                break;
            case 'Escape':
                setSearchSuggestions([]);
                setActiveSearchIndex(-1);
                setSearchValue('');
                break;
        }
    };

    const handleSelectStock = (stock: { ticker_id: string; exchange: string; ticker_symbol: string; company_id: string }) => {
        setSearchSuggestions([]);
        setActiveSearchIndex(-1);
        setSearchValue('');
        setIsSearchFocused(false);
        const params = new URLSearchParams({
            type: "company",
            cid: stock.company_id,
        });
        navigate(
            `/ticker/${stock.ticker_id}/${stock.exchange}/${stock.ticker_symbol}?${params.toString()}`,
            { replace: false, state: { needRefresh: true } }
        );
    };

    useEffect(() => {
        if (fetcher.data && Array.isArray(fetcher.data)) {
            setSearchSuggestions(fetcher.data.slice(0, 5));
        }
    }, [fetcher.data]);

    return (
        <nav id="signed-out-side-navbar" className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-[250px] pt-20 z-30">
            <div className="absolute top-3 left-4">
                <div className="flex flex-1">
                    <a
                        href="/"
                        className="text-inherit no-underline"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate("/", { state: { scrollToHero: true } });
                        }}
                    >
                        <span className="tracking-tight text-base font-bold flex items-center">
                            MarketData
                            <div className="bg-black rounded-md py-[2px] px-[5px] flex items-center justify-center ml-0.5">
                                <span className="text-white text-sm">FYI</span>
                            </div>
                        </span>
                    </a>
                </div>
            </div>

            {/* Inline Search Box */}
            <div className="px-2 mt-10 mb-4">
                <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-gray-900/20 rounded-full blur-md opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchValue}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => {
                            setTimeout(() => {
                                setIsSearchFocused(false);
                                setActiveSearchIndex(-1);
                            }, 200);
                        }}
                        placeholder="Search Ticker..."
                        className="relative w-full px-5 py-2 text-sm font-medium text-white 
                                   bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900
                                   border-2 border-gray-700 rounded-full 
                                   shadow-2xl hover:shadow-xl hover:border-gray-600 hover:from-gray-700 hover:via-gray-800 hover:to-gray-900
                                   focus:outline-none focus:ring-green-500/40 focus:shadow-2xl focus:shadow-green-500/20
                                   focus:from-gray-700 focus:via-gray-800 focus:to-black
                                   transition-all duration-300 pr-12 
                                   placeholder:text-gray-400 placeholder:font-normal"
                        autoComplete="off"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full bg-gray-700 transition-all duration-300 ${isSearchFocused ? 'bg-green-600/80' : ''}`}>
                            <FontAwesomeIcon icon={faMagnifyingGlass} className={`text-gray-300 text-sm transition-all duration-300 ${isSearchFocused ? 'text-white scale-110' : ''}`} />
                        </div>
                    </div>
                    {searchSuggestions.length > 0 && isSearchFocused && (
                        <div className="absolute w-full mt-1 bg-gray-900 border-2 border-gray-700 rounded-xl shadow-2xl overflow-y-auto z-50 max-h-60 backdrop-blur-md ring-2 ring-gray-700/50">
                            {searchSuggestions.map((stock, index) => (
                                <button
                                    key={`${stock.ticker_symbol}-${index}`}
                                    className={`w-full text-left px-4 py-2 hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-700 flex items-center transition-all duration-200 ${index === activeSearchIndex ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-l-[3px] border-l-green-500 shadow-sm' : ''
                                        }`}
                                    onClick={() => handleSelectStock(stock)}
                                    role="option"
                                    aria-selected={index === activeSearchIndex}
                                >
                                    {stock.logo_url && (
                                        <img
                                            src={stock.logo_url}
                                            alt={stock.name}
                                            className="w-5 h-5 mr-2 flex-shrink-0"
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className={`font-medium text-xs truncate ${index === activeSearchIndex ? 'text-white' : 'text-gray-200'}`}>
                                            {stock.ticker_symbol}
                                        </div>
                                        <div className={`text-xs truncate ${index === activeSearchIndex ? 'text-gray-300' : 'text-gray-400'}`}>
                                            {stock.name}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Line divider to separate search from navigation */}
            <div className="mx-4 my-4 border-t border-gray-200"></div>

            <div className="flex flex-col mt-2 px-2">
                {NAV_LINKS.map(({ to, icon, label, isActive, iconClassName = 'text-xl', disabled, disabledLabel, requireAuth }) => {
                    if (disabled) {
                        return (
                            <div
                                key={to}
                                className="flex items-center px-3 py-2.5 text-gray-400 cursor-not-allowed"
                            >
                                <FontAwesomeIcon icon={icon} className={`${iconClassName} mr-3 text-gray-400`} />
                                <span className="text-sm">{label}</span>
                                {disabledLabel && (
                                    <span className="ml-auto text-[10px] font-medium text-gray-400">
                                        {disabledLabel}
                                    </span>
                                )}
                            </div>
                        );
                    }
                    const active = isActive(location.pathname, location.search);
                    const content = (
                        <>
                            <FontAwesomeIcon
                                icon={icon}
                                className={`${iconClassName} mr-3 ${active ? 'text-green-700' : 'text-gray-400'}`}
                            />
                            <span className={`text-sm ${active ? 'text-gray-900' : ''}`}>{label}</span>
                        </>
                    );
                    const activeClassName = active
                        ? 'rounded-l-lg bg-[#e7f2eb] font-bold text-gray-900 border-r-[3px] border-green-700'
                        : 'rounded-lg font-medium text-gray-400 hover:bg-[#e7f2eb]/60 hover:text-gray-900';
                    if (requireAuth && !isAuthenticated) {
                        return (
                            <button
                                key={to}
                                type="button"
                                onClick={() => setIsSignInModalOpen(true)}
                                className={`flex w-full items-center px-3 py-2.5 text-left transition-all duration-200 ${activeClassName}`}
                            >
                                {content}
                            </button>
                        );
                    }
                    return (
                        <Link
                            key={to}
                            to={to}
                            className={`flex items-center px-3 py-2.5 transition-all duration-200 ${activeClassName}`}
                        >
                            {content}
                        </Link>
                    );
                })}
            </div>

            {/* Sign In and Sign Up Buttons */}
            <div className="mt-6 pl-4 pr-20 space-y-5">
                <LinkButton
                    href="/sign-in"
                    type="outline"
                    size="small"
                    fullWidth
                >
                    Sign In
                </LinkButton>
                <LinkButton
                    href="/sign-up"
                    type="primary"
                    size="small"
                    fullWidth
                >
                    Get Started
                </LinkButton>
            </div>

            {isSignInModalOpen && (
                <MainAuthModal
                    isOpen={isSignInModalOpen}
                    onClose={setIsSignInModalOpen}
                    query=""
                />
            )}
        </nav>
    );
};

export default SignedOutSideNavbar; 